import type { CabinetBranchAccess, CabinetMemberView } from "@planerius/contracts/cabinet-auth"
import { type ReactNode, useState } from "react"

import { listMembers } from "../../../api/members.api"
import { Badge } from "../../../ui"
import { useResource } from "../use-resource"
import styles from "./invite-panel.module.css"
import { MemberCard } from "./member-card"
import { branchesWords, lastLoginWords, roleWords, statusBadge } from "./member-model"

/**
 * 4-Б8 «Люди с доступом» — строки в панели «Доступ в кабинет», над приглашениями.
 *
 * Отдельного макета у списка нет (в листах П1 он значится остатком): строка повторяет
 * строку приглашения рядом — имя, роль · филиалы · последний вход, значок состояния.
 * ПОСЛЕДНИЙ ВХОД — не украшение (ТЗ 04, Б8): по нему видны забытые учётки бывших
 * сотрудников. Уволенные — отдельной ссылкой: в общем списке они топили бы действующих,
 * а совсем скрытыми не отвечали бы на «когда и кто закрыл доступ».
 */
export function MembersList(props: {
  readonly branches: readonly CabinetBranchAccess[]
  readonly viewerUserId: string
  readonly canManage: boolean
  readonly timeZone: string
}): ReactNode {
  const [showRemoved, setShowRemoved] = useState(false)
  const members = useResource(
    (signal) => listMembers(showRemoved ? { status: "removed" } : {}, signal),
    showRemoved ? "staff-members-removed" : "staff-members",
  )
  const [open, setOpen] = useState<CabinetMemberView | null>(null)

  const items = members.data?.items ?? []

  return (
    <>
      {items.length === 0 ? null : (
        <ul className={styles.list}>
          {items.map((member) => {
            const badge = statusBadge(member)
            return (
              <li key={member.memberId} className={styles.row}>
                <button type="button" className={styles.memberButton} onClick={() => setOpen(member)}>
                  <span className={styles.rowMain}>
                    <span className={styles.contact}>
                      {member.displayName}
                      {member.userId === props.viewerUserId ? " · это вы" : ""}
                    </span>
                    <span className={styles.meta}>
                      {roleWords(member)} · {branchesWords(member, props.branches)} ·{" "}
                      {lastLoginWords(member, props.timeZone)}
                    </span>
                  </span>
                  {badge === null ? null : <Badge tone={badge.tone}>{badge.label}</Badge>}
                </button>
              </li>
            )
          })}
        </ul>
      )}
      {members.problem === null ? null : (
        <div className={styles.error} role="alert">
          Список людей с доступом не загрузился: {members.problem.detail ?? members.problem.title}{" "}
          <button type="button" className={styles.retry} onClick={members.reload}>
            Повторить
          </button>
        </div>
      )}
      <button type="button" className={`${styles.retry} ${styles.toggle}`} onClick={() => setShowRemoved(!showRemoved)}>
        {showRemoved ? "К действующим доступам" : "Закрытые доступы"}
      </button>

      {open === null ? null : (
        <MemberCard
          member={open}
          branches={props.branches}
          viewerUserId={props.viewerUserId}
          canManage={props.canManage}
          timeZone={props.timeZone}
          onChanged={() => members.reload()}
          onClose={() => setOpen(null)}
        />
      )}
    </>
  )
}
